if (!customElements.get('frame-photo-upload')) {
  customElements.define('frame-photo-upload', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.picker = this.querySelector('[data-upload-picker]');
      this.inputContainer = this.querySelector('[data-upload-inputs]');
      this.list = this.querySelector('[data-upload-list]');
      this.status = this.querySelector('[data-upload-status]');
      this.error = this.querySelector('[data-upload-error]');
      this.later = this.querySelector('[data-upload-later]');
      this.laterProperty = this.querySelector('[data-upload-later-property]');
      this.uploadNow = this.querySelector('[data-upload-now]');
      if (!this.picker || !this.inputContainer || !this.list) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.picker.removeAttribute('name');
      this.picker.addEventListener('change', () => this.addSelection(), options);
      this.later?.addEventListener('change', () => {
        if (this.later.checked) {
          this.selectionError = '';
          this.transferFailed = false;
        }
        this.showError('');
        this.normalizeInputs();
        this.render();
        this.emitChange();
      }, options);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-clear-upload-error]')) {
          this.selectionError = '';
          this.transferFailed = false;
          this.showError('');
          this.render();
          this.emitChange();
          this.picker.focus();
          return;
        }
        const button = event.target.closest('[data-remove-upload]');
        if (!button || !this.contains(button)) return;
        this.removeUpload(Number(button.dataset.removeUpload));
      }, options);
      window.addEventListener('pageshow', () => {
        this.normalizeInputs();
        this.render();
        this.emitChange();
      }, options);
      this.normalizeInputs();
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.releasePreviews();
    }

    get nativeInputs() {
      return [...(this.inputContainer?.querySelectorAll('input[data-upload-file]') || [])];
    }

    get files() {
      return this.nativeInputs.map(input => input.files?.[0]).filter(file => file && this.validFile(file));
    }

    get optional() {
      return Boolean(this.closest('brass-customizer')?.mobileCompact);
    }

    get sendLater() {
      return !this.optional && Boolean(this.later?.checked);
    }

    get errorMessage() {
      if (this.sendLater) return '';
      if (this.files.length) return '';
      if (this.optional && !this.transferFailed) return this.selectionError || '';
      return this.transferFailed ? this.dataset.transferError : this.dataset.requiredError;
    }

    validFile(file) {
      const type = file.type.toLowerCase();
      const knownType = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(type);
      const knownExtension = /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
      return file.size > 0 && file.size <= 20 * 1024 * 1024 &&
        (knownType || ((!type || type === 'application/octet-stream') && knownExtension));
    }

    createFileInput(file) {
      const transfer = new DataTransfer();
      transfer.items.add(file);
      const input = document.createElement('input');
      input.type = 'file';
      input.hidden = true;
      input.dataset.uploadFile = '';
      input.files = transfer.files;
      const retained = input.files?.[0];
      if (!retained || input.files.length !== 1 || retained.name !== file.name ||
          retained.size !== file.size || retained.lastModified !== file.lastModified) {
        throw new Error('File selection could not be retained.');
      }
      return input;
    }

    addSelection() {
      if (this.sendLater) return;
      let selected = [...(this.picker.files || [])];
      if (!selected.length) return;
      if (this.dataset.multiple !== 'true') selected = selected.slice(0, 1);
      const accepted = [];
      const rejected = [];
      const unretained = [];
      selected.forEach(file => {
        if (!this.validFile(file)) {
          rejected.push(file.name);
          return;
        }
        try {
          accepted.push(this.createFileInput(file));
        } catch {
          unretained.push(file.name);
        }
      });
      if (accepted.length) {
        if (this.dataset.multiple !== 'true') this.inputContainer.replaceChildren();
        this.inputContainer.append(...accepted);
        this.normalizeInputs();
      }
      this.transferFailed = unretained.length > 0 && !this.files.length;
      this.picker.value = '';
      const notices = [];
      if (rejected.length) notices.push(this.rejectedMessage(rejected, this.dataset.fileError));
      if (unretained.length) notices.push(this.rejectedMessage(unretained, this.dataset.transferError));
      this.selectionError = notices.join(' ');
      this.showError(this.selectionError);
      this.render();
      if (accepted.length) this.emitChange();
    }

    rejectedMessage(names, error) {
      return (this.dataset.rejectedTemplate || '[names]. [error]')
        .replace('[names]', () => names.join(', ')).replace('[error]', () => error || '');
    }

    normalizeInputs() {
      this.nativeInputs.forEach(input => {
        if (!input.files?.[0] || !this.validFile(input.files[0])) input.remove();
      });
      if (this.dataset.multiple !== 'true') this.nativeInputs.slice(1).forEach(input => input.remove());
      this.nativeInputs.forEach((input, index) => {
        const suffix = this.dataset.multiple === 'true' ? ` ${index + 1}` : '';
        input.name = `properties[${this.dataset.property}${suffix}]`;
        input.disabled = this.sendLater;
      });
    }

    removeUpload(index) {
      const input = this.nativeInputs[index];
      if (!input) return;
      input.remove();
      this.transferFailed = false;
      this.selectionError = '';
      this.normalizeInputs();
      this.showError('');
      this.render();
      this.emitChange();
      const buttons = this.list.querySelectorAll('[data-remove-upload]');
      (buttons[Math.min(index, buttons.length - 1)] || this.picker).focus();
    }

    render() {
      this.releasePreviews();
      this.list.replaceChildren();
      this.previews = [];
      this.files.forEach((file, index) => {
        const row = document.createElement('li');
        row.className = 'brass-uploaded';
        const preview = document.createElement('img');
        preview.width = 72;
        preview.height = 72;
        preview.alt = this.dataset.previewAlt || '';
        preview.hidden = true;
        const copy = document.createElement('div');
        const name = document.createElement('strong');
        name.textContent = file.name;
        const selected = document.createElement('span');
        selected.textContent = this.dataset.selectedLabel || '';
        copy.append(name, selected);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'brass-text-button';
        remove.dataset.removeUpload = String(index);
        remove.textContent = this.dataset.removeLabel || '';
        remove.setAttribute('aria-label', (this.dataset.removeFileTemplate || '[name]').replace('[name]', () => file.name));
        row.append(preview, copy, remove);
        this.list.append(row);
        this.loadPreview(preview, file);
      });
      if (this.uploadNow) {
        this.uploadNow.hidden = this.sendLater;
        this.uploadNow.querySelectorAll('input, button').forEach(control => { control.disabled = this.sendLater; });
      }
      if (this.laterProperty) this.laterProperty.disabled = !(this.sendLater || this.optional && !this.files.length);
      this.list.hidden = !this.files.length;
      if (this.status) {
        const template = this.dataset.countTemplate || this.dataset.addedTemplate || '';
        this.status.textContent = this.files.length ? template.replace('[count]', this.files.length) : '';
      }
    }

    loadPreview(image, file) {
      let url;
      try {
        url = URL.createObjectURL(file);
      } catch {
        return;
      }
      const entry = { image, url };
      this.previews.push(entry);
      image.onload = () => {
        if (entry.url && this.isConnected) image.hidden = false;
      };
      image.onerror = () => {
        image.hidden = true;
        image.removeAttribute('src');
        if (entry.url) URL.revokeObjectURL(entry.url);
        entry.url = null;
        image.onload = null;
        image.onerror = null;
      };
      image.src = url;
    }

    releasePreviews() {
      (this.previews || []).forEach(entry => {
        entry.image.onload = null;
        entry.image.onerror = null;
        entry.image.hidden = true;
        entry.image.removeAttribute('src');
        if (entry.url) URL.revokeObjectURL(entry.url);
        entry.url = null;
      });
      this.previews = [];
    }

    showError(message) {
      if (this.error) {
        this.error.textContent = message || '';
        this.error.hidden = !message;
        if (message && this.optional && !this.files.length && this.dataset.clearErrorLabel) {
          const clear = document.createElement('button');
          clear.type = 'button';
          clear.className = 'brass-text-button';
          clear.dataset.clearUploadError = '';
          clear.textContent = this.dataset.clearErrorLabel;
          this.error.append(' ', clear);
        }
      }
      if (message) this.picker.setAttribute('aria-invalid', 'true');
      else this.picker.removeAttribute('aria-invalid');
    }

    validate() {
      const message = this.errorMessage;
      this.showError(message);
      return !message;
    }

    resetUploads() {
      this.inputContainer.replaceChildren();
      this.picker.value = '';
      this.transferFailed = false;
      this.selectionError = '';
      if (this.later) this.later.checked = false;
      this.showError('');
      this.render();
      this.emitChange();
    }

    emitChange() {
      this.dispatchEvent(new CustomEvent('frame-upload-change', { bubbles: true }));
    }
  });
}
